import { Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import * as React from "react";
import { useContext, useState, useEffect } from "react";
import { DataUser } from "../../context/DataUser";
import { DataPM } from "../../context/DataPM";
import { getActiveProjectsByPM } from "../../utils/project/CRUD";

const ModalDeleteSegments = ({ open, setOpen }) => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [similarSegments, setSimilarSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);

  const { user } = useContext(DataUser);
  const { getSegmentsByProjectsAndDescription } = useContext(DataPM);

  useEffect(() => {
    if (open && user) {
      fetchProjects();
    }
  }, [open, user]);

  useEffect(() => {
    if (filterText) {
      const filtered = projects.filter(project => 
        project.name.toLowerCase().includes(filterText.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [filterText, projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await getActiveProjectsByPM(user.uid);
      
      if (data.status) {
        setProjects(data.projects);
        setFilteredProjects(data.projects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setToastMessage("Error fetching projects");
      setToastSeverity("error");
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (projectId) => {
    setSelectedProjects((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSearchSimilarSegments = async () => {
    if (selectedProjects.length === 0) {
      setToastMessage("Please select at least one project");
      setToastSeverity("error");
      setOpenToast(true);
      return;
    }

    try {
      setLoading(true);
      const response = await getSegmentsByProjectsAndDescription(selectedProjects);
      
      if (response.status && response.segments) {
        // Filter out segments without initial description or with empty description
        const validSegments = response.segments.filter(segment => {
          const hasValidDescription = segment._id && 
                                     segment._id.trim() !== '' && 
                                     segment._id !== 'undefined' && 
                                     segment._id !== 'null';
          const hasSegments = segment.segments && segment.segments.length > 0;
          return hasValidDescription && hasSegments;
        });
        
        setSimilarSegments(validSegments);
        
        if (validSegments.length === 0) {
          setToastMessage("No similar segments found");
          setToastSeverity("info");
          setOpenToast(true);
        } else {
          setToastMessage(`Found ${validSegments.length} similar segment(s)`);
          setToastSeverity("success");
          setOpenToast(true);
        }
      }
    } catch (error) {
      setToastMessage("Error searching similar segments");
      setToastSeverity("error");
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentSelect = (segmentId) => {
    setSelectedSegments((prev) => {
      if (prev.includes(segmentId)) {
        return prev.filter((id) => id !== segmentId);
      } else {
        return [...prev, segmentId];
      }
    });
  };

  const handleDeleteSegments = async () => {
    if (selectedSegments.length === 0) {
      setToastMessage("Please select at least one segment to delete");
      setToastSeverity("error");
      setOpenToast(true);
      return;
    }

    try {
      setLoading(true);
      
      // Get all segment IDs from selected descriptions
      const segmentIdsToDelete = [];
      selectedSegments.forEach(descriptionId => {
        const segmentGroup = similarSegments.find(seg => seg._id === descriptionId);
        if (segmentGroup && segmentGroup.segments) {
          segmentGroup.segments.forEach(segment => {
            if (segment._id) {
              segmentIdsToDelete.push(segment._id);
            }
          });
        }
      });

      if (segmentIdsToDelete.length === 0) {
        setToastMessage("No segments to delete");
        setToastSeverity("error");
        setOpenToast(true);
        setLoading(false);
        return;
      }

      // Delete segments one by one
      let successCount = 0;
      let errorCount = 0;

      for (const segmentId of segmentIdsToDelete) {
        try {
          const response = await fetch(`${process.env.REACT_APP_URL}/api/segments/${segmentId}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          });
          const data = await response.json();
          
          if (data.status) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error deleting segment ${segmentId}:`, error);
          errorCount++;
        }
      }

      // Show results
      if (errorCount === 0) {
        setToastMessage(`Successfully deleted ${successCount} segment(s)`);
        setToastSeverity("success");
        // Clear selections and reset
        setSelectedSegments([]);
        setSimilarSegments([]);
        setSelectedProjects([]);
      } else {
        setToastMessage(`Deleted ${successCount} segment(s), ${errorCount} failed`);
        setToastSeverity("warning");
      }
      setOpenToast(true);
      
    } catch (error) {
      console.error("Error in delete operation:", error);
      setToastMessage("Error deleting segments");
      setToastSeverity("error");
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedProjects([]);
    setFilterText("");
    setSimilarSegments([]);
    setSelectedSegments([]);
  };

  return (
    <>
      <Snackbar
        open={openToast}
        autoHideDuration={3000}
        onClose={() => setOpenToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toastSeverity}>{toastMessage}</Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#28262E",
            color: "white"
          }
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ color: "white", backgroundColor: "#28262E" }}
        >
          Delete multiple segments for projects
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#28262E", padding: 3 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Filter projects by name..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{
                  marginBottom: 3,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'white',
                    },
                    '&:hover fieldset': {
                      borderColor: 'white',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                }}
              />

              <div style={{ width: "100%", marginBottom: "20px" }}>
                <h3>Select Projects ({selectedProjects.length} selected / {filteredProjects.length} total)</h3>
                <div style={{ 
                  display: "flex", 
                  flexWrap: "wrap", 
                  gap: "10px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  padding: "10px"
                }}>
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <Button
                        key={project._id}
                        variant={selectedProjects.includes(project._id) ? "contained" : "outlined"}
                        onClick={() => handleProjectSelect(project._id)}
                        sx={{ 
                          color: "white", 
                          borderColor: "white",
                          backgroundColor: selectedProjects.includes(project._id) ? "#8EC36A" : "transparent",
                          '&:hover': {
                            backgroundColor: selectedProjects.includes(project._id) ? "#7ab358" : "rgba(255, 255, 255, 0.1)"
                          }
                        }}
                      >
                        {project.name}
                      </Button>
                    ))
                  ) : (
                    <div style={{ color: 'white', padding: '20px', textAlign: 'center', width: '100%' }}>
                      No projects found
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSearchSimilarSegments}
                disabled={selectedProjects.length === 0}
                sx={{ 
                  color: "white", 
                  backgroundColor: "#8EC36A", 
                  marginBottom: "20px",
                  '&:hover': {
                    backgroundColor: '#7ab358'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(142, 195, 106, 0.5)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                Search Similar Segments
              </Button>

              {similarSegments.length > 0 && (
                <div style={{ width: "100%", marginTop: "20px" }}>
                  <h3>Similar Segments Found ({selectedSegments.length} selected)</h3>
                  <FormControl fullWidth variant="outlined" sx={{ marginBottom: "20px" }}>
                    <InputLabel id="segment-select-label" sx={{ color: 'white' }}>Select Segments to Delete</InputLabel>
                    <Select
                      labelId="segment-select-label"
                      multiple
                      value={selectedSegments}
                      onChange={(e) => setSelectedSegments(e.target.value)}
                      label="Select Segments to Delete"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: '#28262E',
                            color: 'white',
                            maxHeight: '400px'
                          }
                        }
                      }}
                      sx={{
                        color: 'white',
                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: 'white',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'white',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'white',
                        },
                        '.MuiSvgIcon-root': {
                          color: 'white',
                        }
                      }}
                    >
                      {similarSegments.map((segment) => (
                        <MenuItem 
                          key={segment._id} 
                          value={segment._id}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#3f3f3f'
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#4a4a4a'
                            }
                          }}
                        >
                          {segment._id} ({segment.segments?.length || 0} occurrences)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#28262E",
            padding: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button
            onClick={handleClose}
            sx={{ color: "white", backgroundColor: "#952937" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSegments}
            disabled={selectedSegments.length === 0 || loading}
            sx={{ 
              color: "white", 
              backgroundColor: "#8EC36A",
              '&:hover': {
                backgroundColor: '#7ab358'
              },
              '&:disabled': {
                backgroundColor: 'rgba(142, 195, 106, 0.5)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            Delete Selected Segments
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalDeleteSegments;
